import * as React from 'react';

import { Icon } from '@rbilabs/components-library/build/components/icon';
import { Toggle } from '@rbilabs/components-library/build/components/toggle';
import { VisuallyHidden } from '@rbilabs/components-library/build/components/visually-hidden';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { DeliveryAddressModal } from 'components/address-modal';
import { DeliveryAddressModalProvider } from 'components/address-modal/delivery-address-modal.context';
import { SafeAreaView } from 'components/safe-area-view';
import { useMediaQuery } from 'hooks/use-media-query';
import { routes } from 'utils/routing';

import { SearchBar } from '../search-bar';
import { StoreLocatorSteps, StoreLocatorViews } from '../types';

import { ClearStoreSmButton } from './clear-store-sm-button';
import { EditAddressForm } from './edit-address';
import { FittedViewContainer } from './fitted-view-container';
import { ListView } from './list-view';
import { MapView } from './map-view';
import {
  BackArrow,
  BackHeaderButton,
  ConfirmHeader,
  ConfirmHeaderTitle,
  Header,
  HeaderBackground,
  HeaderWrapper,
  MaxWidth,
  MaxWidthFullHeight,
  SearchBarWrapper,
  SearchContainer,
  ServiceModeCategoryToggleWrapper,
  ToggleViewButtonWrapper,
} from './store-locator.styled';
import { ToggleViewButton } from './toggle-view-button';
import { IStoreLocatorViewProps } from './types';

const StoreLocatorWrapper = styled(SafeAreaView)`
  height: calc(100vh - 51px);
`;

export const StoreLocatorView: React.FC<IStoreLocatorViewProps> = ({
  activeCoordinates,
  errorFavs,
  errorNearby,
  errorRecent,
  handleResetLocation,
  searchingNearby,
  searchingRecent,
  searchingFavs,
  storesFavs = [],
  storesNearby = [],
  storesRecent = [],

  // Map View
  onSearchClick,

  // View
  currentView,
  onViewChange,
  currentStep,
  setCurrentStep,
  onConfirmBackClick,

  // Search Bar
  onAddressSelected,
  onSearchBarValueChange,
  searchBarValue,
  shouldFocusSearchBar,

  // Service Mode Category
  hideServiceModeCategories = false,
  onServiceModeCategoryChange,
  selectedServiceModeCategory,
  serviceModeCategoryOptions = [],

  // Edit Address
  editedAddress,
  setEditedAddress,

  isDeliveryModeSelected,
  deliveryOnSameScreen,
  showStoreLocator,
}) => {
  const { formatMessage } = useIntl();
  const isMobile = useMediaQuery('headerMobile');

  hideServiceModeCategories = showStoreLocator ? hideServiceModeCategories : true;

  const isNotEditingAddress =
    currentStep !== StoreLocatorSteps.EDIT_ADDRESS &&
    !(
      isMobile &&
      [StoreLocatorSteps.SAVED_ADDRESS, StoreLocatorSteps.CONFIRM_ADDRESS].includes(currentStep)
    );

  return (
    <StoreLocatorWrapper>
      <FittedViewContainer data-testid="store-locator-wrapper">
        {isNotEditingAddress && (
          <HeaderBackground>
            <HeaderWrapper>
              <ClearStoreSmButton />

              <Header>{formatMessage({ id: 'locations' })}</Header>
              {showStoreLocator && (
                <ToggleViewButtonWrapper>
                  <ToggleViewButton
                    currentView={currentView}
                    updateStoreLocatorView={onViewChange}
                  />
                </ToggleViewButtonWrapper>
              )}
            </HeaderWrapper>
          </HeaderBackground>
        )}

        {currentStep === StoreLocatorSteps.EDIT_ADDRESS && (
          <HeaderBackground>
            <HeaderWrapper>
              <BackHeaderButton
                onClick={() => {
                  setCurrentStep(StoreLocatorSteps.ADDRESS_SELECTOR);
                }}
              >
                <Icon icon="back" color="white" width="24px" aria-hidden />
                <VisuallyHidden>{formatMessage({ id: 'back' })}</VisuallyHidden>
              </BackHeaderButton>

              <Header>{formatMessage({ id: 'editAddress' })}</Header>
            </HeaderWrapper>
          </HeaderBackground>
        )}

        {currentStep === StoreLocatorSteps.CONFIRM_ADDRESS && isMobile && (
          <ConfirmHeader>
            <BackArrow onClick={onConfirmBackClick}>
              <Icon icon="back" color="icon-header-contrast" width="24px" aria-hidden />
              <VisuallyHidden>{formatMessage({ id: 'back' })}</VisuallyHidden>
            </BackArrow>
            <ConfirmHeaderTitle>
              {formatMessage({ id: 'deliveryConfirmDetails' })}
            </ConfirmHeaderTitle>
          </ConfirmHeader>
        )}

        {currentStep === StoreLocatorSteps.SAVED_ADDRESS && isMobile && (
          <ConfirmHeader>
            <BackArrow onClick={onConfirmBackClick}>
              <Icon icon="back" color="icon-header-contrast" width="24px" aria-hidden />
              <VisuallyHidden>{formatMessage({ id: 'back' })}</VisuallyHidden>
            </BackArrow>
            <ConfirmHeaderTitle>{formatMessage({ id: 'savedAddresses' })}</ConfirmHeaderTitle>
          </ConfirmHeader>
        )}

        {currentStep === StoreLocatorSteps.EDIT_ADDRESS && (
          <DeliveryAddressModalProvider>
            {editedAddress && (
              <EditAddressForm
                setCurrentStep={setCurrentStep}
                address={editedAddress}
                setEditedAddress={setEditedAddress}
              />
            )}
          </DeliveryAddressModalProvider>
        )}

        {currentStep !== StoreLocatorSteps.EDIT_ADDRESS && (
          <>
            <MaxWidth>
              <SearchContainer>
                {!hideServiceModeCategories ? (
                  <ServiceModeCategoryToggleWrapper>
                    <Toggle
                      data-testid="service-mode-category-toggle"
                      options={serviceModeCategoryOptions}
                      onChange={onServiceModeCategoryChange}
                      value={selectedServiceModeCategory}
                    />
                  </ServiceModeCategoryToggleWrapper>
                ) : null}

                <SearchBarWrapper $showStoreLocator={showStoreLocator}>
                  {isDeliveryModeSelected ? (
                    <DeliveryAddressModalProvider>
                      <DeliveryAddressModal
                        redirectUrl={routes.menu}
                        editedAddress={editedAddress}
                        setEditedAddress={setEditedAddress}
                        dismissAddressModalUrl={routes.storeLocator}
                        renderInPage={deliveryOnSameScreen}
                        currentStep={currentStep}
                        setCurrentStep={setCurrentStep}
                      />
                    </DeliveryAddressModalProvider>
                  ) : (
                    showStoreLocator && (
                      <SearchBar
                        autoFocus={shouldFocusSearchBar}
                        value={searchBarValue}
                        onInputValueChange={onSearchBarValueChange}
                        onAddressSelected={onAddressSelected}
                      />
                    )
                  )}
                </SearchBarWrapper>
              </SearchContainer>
            </MaxWidth>

            {/* ListView can be mounted and unmounted. */}
            {!isDeliveryModeSelected && currentView === StoreLocatorViews.LIST_VIEW ? (
              <MaxWidth>
                <ListView
                  {...{
                    errorFavs,
                    errorNearby,
                    errorRecent,
                    searchingNearby,
                    searchingRecent,
                    searchingFavs,
                    storesFavs,
                    storesNearby,
                    storesRecent,
                  }}
                />
              </MaxWidth>
            ) : null}

            <MaxWidthFullHeight>
              {/* MapView remains mounted and manages showing and hiding internally. */}
              <MapView
                currentViewIsMap={currentView === StoreLocatorViews.MAP_VIEW}
                {...{
                  storesFavs,
                  storesNearby,
                  storesRecent,
                  activeCoordinates,
                  onSearchClick,
                  handleResetLocation,
                }}
              />
            </MaxWidthFullHeight>
          </>
        )}
      </FittedViewContainer>
    </StoreLocatorWrapper>
  );
};
